import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast, Toaster } from 'react-hot-toast';
import { ProgressBar } from './components/ProgressBar';
import { CadastroTableStep } from './steps/CadastroTableStep';
import { QuotaTableStep } from './steps/QuotaTableStep';
import { ProductionUploadStep } from './steps/ProductionUploadStep';
import { WizardProvider, useWizard } from './context/WizardCtx';
import { loadDraft, hasDraft } from './utils/draftStorage';
import { WizardStep, DayColumn } from './types/wizard';
import { SecondaryButton } from './components/buttons';
import { ArrowLeft } from 'lucide-react';

/**
 * SalesQuotaWizard props
 */
interface SalesQuotaWizardProps {
    onScheduleGenerated?: (schedule: DayColumn[]) => void;
    onClose?: () => void;
    shouldReset?: boolean;
}

/**
 * Internal wizard content component
 */
const WizardContent: React.FC<SalesQuotaWizardProps> = ({
    onScheduleGenerated,
    onClose,
    shouldReset = false,
}) => {
    const { data, setData, resetWizard } = useWizard();
    const [currentStep, setCurrentStep] = useState<WizardStep>(1); // Start with step 1
    const [isLoading, setIsLoading] = useState(true);

    // Log changes to currentStep
    useEffect(() => {
        console.log('📊 Current step changed to:', currentStep);
    }, [currentStep]);

    /**
     * Reset wizard if shouldReset is true
     */
    useEffect(() => {
        console.log('🔄 Reset useEffect triggered, shouldReset:', shouldReset);
        if (shouldReset) {
            console.log('🔄 Resetting wizard and step to 1');
            resetWizard();
            setCurrentStep(1); // Reset to step 1
        }
    }, [shouldReset]);

    /**
     * Load draft data on mount
     */
    useEffect(() => {
        console.log('📂 Draft loading useEffect triggered, shouldReset:', shouldReset);
        if (shouldReset) {
            // Skip loading draft if we should reset
            console.log('📂 Skipping draft load because shouldReset is true');
            setIsLoading(false);
            return;
        }

        const loadInitialData = async () => {
            try {
                console.log('📂 Loading draft data...');
                const savedDraft = await loadDraft();
                if (savedDraft) {
                    console.log('📂 Draft found, savedStep:', savedDraft.savedStep);
                    setData(savedDraft);
                    setCurrentStep(savedDraft.savedStep || 1); // Default to step 1
                    toast.success('Rascunho carregado automaticamente');
                } else {
                    console.log('📂 No draft found');
                }
            } catch (error) {
                console.error('📂 Failed to load draft:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadInitialData();
    }, [shouldReset]);

    /**
     * Navigate to next step
     */
    const goToNextStep = () => {
        // Validate step 2 requirements before advancing to step 3
        if (currentStep === 2) {
            if (!data.maxDailyVolume || data.maxDailyVolume <= 0) {
                toast.error('É necessário fazer upload da planilha de produção e aguardar o cálculo do limite diário.');
                return;
            }
        }

        if (currentStep < 3) {
            const nextStep = (currentStep + 1) as WizardStep;
            console.log('🚀 Moving to step:', nextStep);
            setCurrentStep(nextStep);
            console.log('✅ Step updated successfully');
        } else {
            console.log('⚠️ Already at last step, cannot advance');
        }
    };

    /**
     * Navigate to previous step
     */
    const goToPreviousStep = () => {
        if (currentStep > 1) { // Allow going back to step 1
            setCurrentStep((prev) => (prev - 1) as WizardStep);
        }
    };

    /**
     * Handle schedule generation completion
     */
    const handleScheduleGenerated = (schedule: DayColumn[]) => {
        // Don't store schedule data in state or show success screen
        // Just pass through to parent callback immediately
        if (onScheduleGenerated) {
            onScheduleGenerated(schedule);
        }
    };

    /**
     * Handle wizard close
     */
    const handleClose = () => {
        resetWizard();
        if (onClose) {
            onClose();
        }
    };

    /**
     * Get appropriate back handler for current step
     */
    const getBackHandler = () => {
        if (currentStep === 1) { // Now step 1 is the first step
            return handleClose;
        } else {
            return goToPreviousStep;
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-96 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-dexco-green-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-600 font-body">Carregando assistente...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-dexco-gray-bg">
            <div className="max-w-4xl mx-auto px-1 sm:px-6 py-6">
                {/* Top Navigation */}
                <div className="flex items-center justify-between mb-8">
                    <SecondaryButton
                        onClick={getBackHandler()}
                        leftIcon={<ArrowLeft className="w-4 h-4" />}
                        aria-label={currentStep === 1 ? "Voltar para a página inicial" : "Voltar para o passo anterior"}
                    >
                        {currentStep === 1 ? 'Voltar ao Início' : 'Voltar'}
                    </SecondaryButton>

                    {/* Step indicator in top right */}
                    <div className="text-sm text-gray-600 font-body">
                        Passo {currentStep} de 3
                    </div>
                </div>

                {/* Progress Bar */}
                <ProgressBar currentStep={currentStep} className="mb-8" />

                {/* Step Content */}
                <div className="min-h-96">
                    {currentStep === 1 && (
                        <CadastroTableStep
                            key="step0"
                            onNext={goToNextStep}
                        />
                    )}
                    {currentStep === 2 && (
                        <QuotaTableStep
                            key="step1"
                            onNext={goToNextStep}
                        />
                    )}
                    {currentStep === 3 && (
                        <ProductionUploadStep
                            key="step3"
                            onScheduleGenerated={handleScheduleGenerated}
                            onGoHome={onClose}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * Hook to check if there's a saved draft
 */
export const useDraftStatus = () => {
    const [hasSavedDraft, setHasSavedDraft] = useState(false);
    const [isChecking, setIsChecking] = useState(true);

    useEffect(() => {
        const checkDraft = async () => {
            try {
                const draftExists = await hasDraft();
                setHasSavedDraft(draftExists);
            } catch (error) {
                console.error('Failed to check draft:', error);
            } finally {
                setIsChecking(false);
            }
        };

        checkDraft();
    }, []);

    return { hasSavedDraft, isChecking };
};

/**
 * Main SalesQuotaWizard component with provider wrapper
 */
export const SalesQuotaWizard: React.FC<SalesQuotaWizardProps> = (props) => {
    return (
        <WizardProvider>
            <div className="max-w-6xl mx-auto px-1 sm:px-6 py-6 bg-dexco-gray-bg min-h-screen">
                {/* Header */}
                <div className="text-center mb-8">
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4"
                    >
                        {/* Dexco Logo Icon */}
                        <div className="flex justify-center mb-6">
                            <img
                                src="/logo-ico.svg"
                                alt="Dexco"
                                className="h-12 w-12"
                            />
                        </div>

                        <h1 className="font-display font-semibold text-3xl md:text-4xl text-dexco-text-dark">
                            Assistente de Priorização
                        </h1>
                        <p className="text-gray-600 font-body text-lg max-w-2xl mx-auto">
                            Configure suas cotas de vendedores, limites de clientes e faça upload
                            da produção planejada para gerar uma priorização otimizada.
                        </p>
                    </motion.div>
                </div>

                {/* Wizard Content */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-dexco-white rounded-xl shadow-lg p-8"
                >
                    <WizardContent {...props} />
                </motion.div>

                {/* Toast Notifications */}
                <Toaster
                    position="top-right"
                    toastOptions={{
                        duration: 4000,
                        style: {
                            background: '#FFFFFF',
                            color: '#1A1A1A',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                            fontFamily: 'Inter, sans-serif',
                        },
                        success: {
                            iconTheme: {
                                primary: '#00684A',
                                secondary: '#FFFFFF',
                            },
                        },
                        error: {
                            iconTheme: {
                                primary: '#EF4444',
                                secondary: '#FFFFFF',
                            },
                        },
                    }}
                />
            </div>
        </WizardProvider>
    );
}; 